import React, { useState, useEffect } from "react";
import {
  Box, Paper, Grid, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TablePagination, TableRow, TableSortLabel, Button, Stack, 
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress,
} from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { DataGrid } from "@mui/x-data-grid";
import PageLoader from "../../../components/Loader/PageLoader"
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HeaderName from "../../../components/Custom/HeaderName";
import { EditIcon } from "../../../components/Icons";
import { storeEditCustomerDetails } from "../../../redux/Slices/customerSlice";
import Swal from "sweetalert2";
import { deletePickupDetails, getPickupDetails, createPickupDetails } from "../../../redux/Thunk/officePickupThunk";
import moment from "moment/moment";
import Papa from 'papaparse';
import { State } from "country-state-city";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};

const PickupList =() =>{
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [loader, setLoader] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const role = localStorage.getItem('role');
  const [selectedRows, setSelectedRows] = useState([]);
  const [search,setSearch] = useState('');
  const [PickupList, setPickupList] = useState([])
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ success: 0, failed: 0, total: 0 });
  
  const debouncedSearch = useDebounce(search, 500);
  
  // pagination
  const [page, setPage] = useState({
    page:0,
    pageSize:10
  })

  useEffect(()=>{
    fetchPickup()
  },[page ,debouncedSearch])

  // get all customer details
  const fetchPickup = async () => {
    setLoader(true)
    dispatch(getPickupDetails({page: page.page + 1, limit: page.pageSize, search: debouncedSearch, OfficeId:'', isAllocated:'',Status:"",reverse:true})).then((res) => {
      setPickupList(res?.payload?.data)
      setTotalCount(res?.payload?.total)
      setLoader(false)
    })
  };

  const handleRowSelection = (selection) => {
    if (selection?.length === 0) {
      setSelectedRows([]);
    } else {
      setSelectedRows(selectedRows);
    }
  };

  // Edit Customer details
  const handleClickEditPickup =(details) =>{
    navigate('/dashboard/pickuplist/newpickup', {state:{editDetails:details}})
  }

  // Deleted Customer details
  const handleClickDeletePickup =(row) =>{
    Swal.fire({ 
      title: `Are you sure you want to delete the selected Pickup?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return dispatch(deletePickupDetails(row?.RequestedId)).then((res)=>{
          if(res?.payload?.success === true){
            Swal.fire('Deleted!', res?.payload?.message, 'delete');
            setSelectedRows([])
          }else{
            Swal.fire('Error!', res?.payload?.message, 'error');
          }
          fetchPickup()
        });
      },
    });
  }

  // CSV Upload Functions
  const requiredFields = [
    'OfficeId', 'Customer', 'GrossWeight', 'NoOfPcs', 'DataLogger', 
    'DataLoggerValue', 'Content', 'PackegeType', 'Remark', 'Consigner',
    'Consignee', 'ReceiverName', 'PickupDate', 'PickupTime', 'ReceiverMobile',
    'SenderName', 'SenderMobile', 'OriginState', 'DestinationState',
    'Origin', 'Destination', 'PickupAddress', 'DestinationAddress'
  ];

  const validateCSVHeaders = (headers) => {
    const missingFields = requiredFields.filter(field => !headers.includes(field));
    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };

      const { officeList } = useSelector((state) => state.office)
      const { CustomerList } = useSelector((state) => state.customer)
      const transformCSVDataToPickupFormat = (csvRow) => {
    // Transform CSV data to match the required API format
  const matchedOffice = officeList?.data?.find(
    (item) => item.OfficeName?.toLowerCase().includes(csvRow.OfficeId?.toLowerCase() || "")
  );

  const matchedCustomer = CustomerList?.data?.find(
    (item) => item.CustomerName?.toLowerCase().includes(csvRow.Customer?.toLowerCase() || "")
  );    
    const originStateObj = State.getStatesOfCountry("IN").find(
      (st) =>
        st.name.toLowerCase() === (csvRow.OriginState || "").toLowerCase()
    );

    const destinationStateObj = State.getStatesOfCountry("IN").find(
      (st) =>
        st.name.toLowerCase() === (csvRow.DestinationState || "").toLowerCase()
    );
    let pickupDateTime = new Date().toISOString(); // default
    if (csvRow.PickupDate && csvRow.PickupTime) {
      const [time, modifier] = csvRow.PickupTime.split(' '); // "2:02 PM" -> ["2:02", "PM"]
      let [hours, minutes] = time.split(':').map(Number);

      if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
      if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;

      const [year, month, day] = csvRow.PickupDate.split('-').map(Number);
      // month is 0-indexed in JS Date
      const dateObj = new Date(year, month - 1, day, hours, minutes);
      pickupDateTime = dateObj.toISOString();
    }

  return {
      OfficeId: matchedOffice?.OfficeId || "",
      Customer: matchedCustomer?.CustomerId || "",
      GrossWeight: csvRow.GrossWeight || "",
      NoOfPcs: csvRow.NoOfPcs || "",
      DataLogger: csvRow.DataLogger || "",
      DataLoggerValue: csvRow.DataLoggerValue || "",
      Content: csvRow.Content || "",
      PackegeType: csvRow.PackegeType || "",
      Remark: csvRow.Remark || "",
      Consigner: csvRow.Consigner || "",
      Consignee: csvRow.Consignee || "",
      ReceiverName: csvRow.ReceiverName || "",
      PickupDateAndTime: pickupDateTime || csvRow.PickupDateAndTime || new Date().toISOString(),
      ReceiverMobile: csvRow.ReceiverMobile || "",
      SenderName: csvRow.SenderName || "",
      SenderMobile: csvRow.SenderMobile || "",
      OriginState: originStateObj?.isoCode || csvRow.OriginState,
      DestinationState: destinationStateObj?.isoCode || csvRow.DestinationState,
      Origin: csvRow.Origin || "",
      Destination: csvRow.Destination || "",
      PickupAddress: csvRow.PickupAddress || "",
      DestinationAddress: csvRow.DestinationAddress || "",
      PackageDetails: csvRow.PackageDetails ? JSON.parse(csvRow.PackageDetails) : [],
      SenderEmail: csvRow.SenderEmail ? csvRow.SenderEmail.split(',').map(email => email.trim()) : [],
      ReceiverEmail: csvRow.ReceiverEmail ? csvRow.ReceiverEmail.split(',').map(email => email.trim()) : [],
    };
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset upload stats
    setUploadStats({ success: 0, failed: 0, total: 0 });
    setUploadProgress(0);
    setIsUploading(true);
    setUploadDialogOpen(true);

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        const { data, errors, meta } = results;
        
        // Validate headers
        const headerValidation = validateCSVHeaders(meta.fields || []);
        if (!headerValidation.isValid) {
          Swal.fire({
            title: 'Invalid CSV Format',
            text: `Missing required fields: ${headerValidation.missingFields.join(', ')}`,
            icon: 'error'
          });
          setIsUploading(false);
          setUploadDialogOpen(false);
          return;
        }

        const validRows = data.filter(row => 
          row.OfficeId && row.Customer && row.GrossWeight && row.PickupDate && row.PickupTime
        );

        setUploadStats(prev => ({ ...prev, total: validRows.length }));

        // Process rows one by one
        for (let i = 0; i < validRows.length; i++) {
          try {
            const row = validRows[i];
            const pickupData = transformCSVDataToPickupFormat(row);
            await dispatch(createPickupDetails(pickupData)).then((res) => {
              if (res?.payload?.success) {
                setUploadStats(prev => ({ ...prev, success: prev.success + 1 }));
              } else {
                setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
              }
            });
            
            // Update progress
            const progress = ((i + 1) / validRows.length) * 100;
            setUploadProgress(progress);
            
          } catch (error) {
            console.error('Error creating pickup:', error);
            setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
          }
        }

        setIsUploading(false);
        setUploadDialogOpen(false);
        // Show completion message
        setTimeout(() => {
          Swal.fire({
            title: 'Upload Complete',
            html: `
              <div>
                <p>Total Records: ${validRows.length}</p>
                <p style="color: green;">Successful: ${uploadStats.success}</p>
                <p style="color: red;">Failed: ${uploadStats.failed}</p>
              </div>
            `,
            icon: 'info'
          });
          fetchPickup(); // Refresh the list
        }, 1000);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to parse CSV file',
          icon: 'error'
        });
        setIsUploading(false);
        setUploadDialogOpen(false);
      }
    });

    // Reset file input
    event.target.value = '';
  };

  const columns = [
    {
      flex: 0.1,
      minWidth: 80,
      field: 'Sr.No',
      align:'center',
      headerName: 'Sr. No',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', padding:1, alignItems: 'center',justifyContent: 'center' }}>
            <Typography noWrap variant='body2' sx={{}}>
              {page?.page * page?.pageSize + params.api.getAllRowIds().indexOf(params.id)+1}
            </Typography>
          </Box>
        )
      }
    },
    {
      flex: 0.25,
      minWidth: 140,
      field: 'PickupId',
      headerName: 'Id',
      align:'center',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'center' }}>
            <Typography noWrap variant='body2'>
              {row?.RequestedId}
            </Typography>
          </Box>
        )
      }
    },
     {
      flex: 0.25,
      minWidth: 120,
      field: 'DocketId',
      headerName: 'Docket Id',
      align:'center',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'center' }}>
            <Typography noWrap variant='body2'>
              {row?.DocketId}
            </Typography>
          </Box>
        )
      }
    },
    {
      flex: 0.35,
      minWidth: 150,
      field: 'Name',
      headerName: 'Customer Name',
      align:'center',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography noWrap variant='body2' sx={{ maxWidth: '100%', whiteSpace: 'pre-line', wordBreak: 'break-all', flex: 1}}>
              {row?.Customers[0]?.CustomerName || '--'}
            </Typography>
          </Box>
        )
      }
    }, 
    {
      flex: 0.4,
      minWidth: 180,
      field: 'Origin',
      headerName: 'Origin City',
      align:'center',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography noWrap variant='body2' sx={{ maxWidth: '100%', whiteSpace: 'pre-line', wordBreak: 'break-all', flex: 1}}>
              {row?.Origin || '--'}
            </Typography>
          </Box>
        )
      }
    },
    {
      flex: 0.5,
      minWidth: 180,
      field: 'Destination',
      align:'center',
      headerName: 'Destination City',
      renderCell: params => {
        const { row } = params
  
        return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography noWrap variant='body2' sx={{maxWidth: '100%', whiteSpace: 'pre-line', wordBreak: 'break-all', flex: 1}}>
              {row?.Destination || '--'}
            </Typography>
          </Box>
        </>
        )
      }
    },
    {
      flex: 0.25,
      minWidth: 180,
      field: 'PickupDateAndTime',
      align:'center',
      headerName: 'Date',
      renderCell: params => {
        const { row } = params
  
        return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography noWrap variant='body2' sx={{maxWidth: '100%', whiteSpace: 'pre-line', wordBreak: 'break-all', flex: 1}}>
              {moment(row?.PickupDateAndTime).format('D/M/YYYY')}
            </Typography>
          </Box>
        </>
        )
      }
    },
    {
      flex: 0.25,
      minWidth: 150,
      field: 'Remark',
      align:'center',
      headerName: 'Remark',
      renderCell: params => {
        const { row } = params
  
        return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography noWrap variant='body2' sx={{ maxWidth: '100%', whiteSpace: 'pre-line', wordBreak: 'break-all', flex: 1}}>
              {row?.Remark || ''}
            </Typography>
          </Box>
        </>
        )
      }
    },
    {
      flex: 0.25,
      minWidth: 150,
      field: 'Status',
      align:'center',
      headerName: 'Status',
      renderCell: params => {
        const { row } = params
  
        return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography noWrap variant='body2' sx={{ maxWidth: '100%', whiteSpace: 'pre-line', wordBreak: 'break-all', flex: 1}}>
              {row?.Status || ''}
            </Typography>
          </Box>
        </>
        )
      }
    },
    {
      flex: 0.1,
      minWidth: 80,
      field: 'Action',
      headerName: 'Action',
      align:'center',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography noWrap variant='body2'>
                <IconButton id='edit' onClick={() => row?.Status ==='Delivered' ? '': handleClickEditPickup(row)}><EditIcon w={18} h={18} enabled={row?.Status ==='Delivered' ? false : true}/></IconButton>
                <IconButton id='delete' onClick={()=> row?.Status ==='Delivered' ? '': handleClickDeletePickup(row)}><DeleteIcon fontSize="medium" style={{color:row?.Status ==='Delivered' ? '#808080' : '#04AB44'}}/></IconButton>
              </Typography>
            </Box>
          </Box>
        )
      }
    },    
  ]

  return(
    <>
      <div style={{display:'flex', flexDirection:'column', flexGrow: 1}}>
        { loader ? <PageLoader /> : 
          <Grid container style={{padding:'0px 10px', width: '85%', display:'flex', flexDirection:'row', justifyContent:'space-between'}}>   
            <Grid size={{ xs: 12, sm: 12, md: 12, lg:12 }}>
              <HeaderName 
                headerName={'Pickup List'}  
                displaySearch={true} 
                newForm={true} 
                createNewForm={()=> navigate('/dashboard/pickuplist/newpickup')} 
                onClickBackButton={()=> navigate("/dashboard")} 
                setSearch={setSearch} 
                search={search}  
              />
              
              {/* Upload CSV Button */}
              <Stack direction="row" justifyContent="center" alignItems="center" mb={2} mt={-6} ml={40}>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="csv-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="csv-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                      },
                    }}
                  >
                    Upload CSV
                  </Button>
                </label>              
                </Stack>

              <div className="custom-data-grid-container_table">
                <DataGrid
                  sx={{
                    borderRadius: "31px",
                    overflow: "auto"
                  }}
                  style={{ width: '100%' }}
                  pagination
                  rows={PickupList}
                  rowHeight={55}
                  rowCount={totalCount}
                  getRowId={param=> param._id}
                  columns={columns}
                  paginationModel={page}
                  sortingMode='server'
                  paginationMode='server'
                  pageSizeOptions={[10, 50, 100]}
                  onPaginationModelChange={(newPage)=>setPage(newPage)}
                  className="custom-data-grid"
                  selectionModel={selectedRows ? [selectedRows] : []}
                  onRowSelectionModelChange={handleRowSelection}
                  initialState={{
                    totalCount,
                    pagination: { paginationModel: {page:1, pageSize: 10}},
                  }}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSorting
                />
              </div>
            </Grid>
          </Grid>
        }

        {/* Upload Progress Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => !isUploading && setUploadDialogOpen(false)}>
          <DialogTitle>
            {isUploading ? 'Uploading CSV...' : 'Upload Complete'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ height: 10, borderRadius: 5 }}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                {Math.round(uploadProgress)}% Complete
              </Typography>
            </Box>
            
            {!isUploading && (
              <Box>
                <Typography variant="body1">
                  Total Records: {uploadStats.total}
                </Typography>
                <Typography variant="body1" color="success.main">
                  Successful: {uploadStats.success}
                </Typography>
                <Typography variant="body1" color="error.main">
                  Failed: {uploadStats.failed}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setUploadDialogOpen(false)} 
              disabled={isUploading}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  )
}

export default PickupList